import { useMemo } from 'react';
import { useAuth } from '@/contexts/auth';
import { calculateFeatureAccess } from '@/utils/user/featureAccess';
import { usePrayerCompanionUsage } from './usePrayerCompanionUsage';
import type { PrayerCompanionMode } from '@/types/prayerCompanion.types';

/**
 * Return type for the Prayer Companion access control hook
 */
export interface UsePrayerCompanionAccessReturn {
  // Access permissions for each mode
  canAccessCards: boolean;
  canAccessChat: boolean;
  canAccessPrayer: boolean;
  canAccessRealtime: boolean;
  
  // Usage limits and tracking
  dailyInteractionLimit: number;
  hasUnlimitedInteractions: boolean;
  
  // Mode-specific access checks
  canAccessMode: (mode: PrayerCompanionMode) => boolean;
  hasReachedDailyLimit: (mode: PrayerCompanionMode) => boolean;
  
  // Combined access validation
  canInteractWithMode: (mode: PrayerCompanionMode) => boolean;
  
  // Utility functions
  getRemainingInteractions: (mode: PrayerCompanionMode) => number;
  getAccessDeniedReason: (mode: PrayerCompanionMode) => 'NO_ACCESS' | 'DAILY_LIMIT' | 'ACCESSIBLE';
  
  // Loading states
  isLoading: boolean;
}

/**
 * Hook for managing Prayer Companion access control
 * Combines feature access permissions with daily usage limits
 */
export function usePrayerCompanionAccess(): UsePrayerCompanionAccessReturn {
  const { profile } = useAuth();
  const { 
    dailyUsage, 
    isLoading: isUsageLoading, 
    canInteract,
    getRemainingInteractions: getUsageRemainingInteractions
  } = usePrayerCompanionUsage();

  // Convert Profile to UserProfile format for feature access calculation
  const userProfile = useMemo(() => {
    if (!profile) return null;
    
    return {
      id: profile.id,
      email: profile.email || '',
      username: profile.username || null,
      role: profile.role_text || 'free',
      name: profile.username || profile.email || '',
      avatarUrl: profile.avatar_url,
      createdAt: profile.created_at || '',
      updatedAt: profile.updated_at,
      isLoggedIn: true,
      isAdmin: profile.role_text === 'staff_admin' || profile.role_text === 'super_admin',
      isNewUser: false,
      displayName: profile.full_name || profile.username || profile.email || ''
    };
  }, [profile]);

  // Get user's feature access
  const featureAccess = useMemo(() => 
    calculateFeatureAccess(userProfile), 
    [userProfile]
  );

  // Extract prayer companion access
  const prayerCompanionAccess = featureAccess.prayerCompanion;

  /**
   * Check if user can access a specific companion mode
   */
  const canAccessMode = (mode: PrayerCompanionMode): boolean => {
    switch (mode) {
      case 'cards':
        return prayerCompanionAccess.canAccessCards;
      case 'chat':
        return prayerCompanionAccess.canAccessChat;
      case 'prayer':
        return prayerCompanionAccess.canAccessPrayer;
      case 'realtime':
        return prayerCompanionAccess.canAccessRealtime;
      default:
        return false;
    }
  };

  /**
   * Check if user has reached daily limit for a specific mode
   */
  const hasReachedDailyLimit = (mode: PrayerCompanionMode): boolean => {
    // Unlimited interactions
    if (prayerCompanionAccess.dailyInteractionLimit === -1) {
      return false;
    }
    
    // Check current usage against limit
    return !canInteract(mode);
  };

  /**
   * Combined check: user has access AND hasn't reached daily limit
   */
  const canInteractWithMode = (mode: PrayerCompanionMode): boolean => {
    return canAccessMode(mode) && !hasReachedDailyLimit(mode);
  };

  /**
   * Get remaining interactions for a specific mode
   */
  const getRemainingInteractions = (mode: PrayerCompanionMode): number => {
    if (!canAccessMode(mode)) return 0;
    if (prayerCompanionAccess.dailyInteractionLimit === -1) return Infinity;
    
    return getUsageRemainingInteractions(mode);
  };

  /**
   * Get the reason why access might be denied
   */
  const getAccessDeniedReason = (mode: PrayerCompanionMode): 'NO_ACCESS' | 'DAILY_LIMIT' | 'ACCESSIBLE' => {
    if (!canAccessMode(mode)) {
      return 'NO_ACCESS';
    }
    
    if (hasReachedDailyLimit(mode)) {
      return 'DAILY_LIMIT';
    }
    
    return 'ACCESSIBLE';
  };

  return {
    // Direct access permissions
    canAccessCards: prayerCompanionAccess.canAccessCards,
    canAccessChat: prayerCompanionAccess.canAccessChat,
    canAccessPrayer: prayerCompanionAccess.canAccessPrayer,
    canAccessRealtime: prayerCompanionAccess.canAccessRealtime,
    
    // Usage limits
    dailyInteractionLimit: prayerCompanionAccess.dailyInteractionLimit,
    hasUnlimitedInteractions: prayerCompanionAccess.dailyInteractionLimit === -1,
    
    // Functions
    canAccessMode,
    hasReachedDailyLimit,
    canInteractWithMode,
    getRemainingInteractions,
    getAccessDeniedReason,
    
    // Loading state
    isLoading: isUsageLoading
  };
}